import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import B2bCommonApi from 'api/b2b/B2bCommonApi'
import B2bTDPAPI from 'api/b2b/TdpApi'
import TechnologyCompanyApi from 'api/b2b/TechnologyCompanyApi'
import OrbitSession from 'hooks/useCookies'
import { debounce } from 'lodash'

import {
  BusinessTypes,
  commercialTypes,
  CurrencySupported,
  ReferralSource,
  TDPRegistrationStatus
} from '../Registration/contants'

import CompaniesTable from './components/TdpAgentTable'

function TDPManagement() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useState<
    Record<string, string> | undefined
  >(undefined)
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      companyName: '',
      contactPersonFirstName: '',
      contactPersonLastName: '',
      country: null,
      city: null,
      currency: null,
      businessType: null,
      refSource: null,
      '3ptp': null,
      commercialType: commercialTypes[0] || null,
      accountManager: { label: 'Shreyansh', value: 'shreyansh' },
      status: TDPRegistrationStatus[0] || null
    }
  })
  const [shouldFetch, setShouldFetch] = useState(false)

  const countryWatch = watch('country')

  const [techInput, setTechInput] = useState('')
  const [debouncedTechTerm, setDebouncedTechTerm] = useState('')
  const debouncedSetTechTerm = useMemo(
    () => debounce((term: string) => setDebouncedTechTerm(term), 500),
    []
  )

  useEffect(() => {
    debouncedSetTechTerm(techInput.trim())
    return () => debouncedSetTechTerm.cancel()
  }, [techInput, debouncedSetTechTerm])

  const { data: techCompanyOptions = [], isLoading: techCompanyLoading } =
    useQuery(
      ['techCompanies', debouncedTechTerm],
      async () => {
        const token = OrbitSession.get('faberToken')

        const { data } = await TechnologyCompanyApi.getTechnologyCompanyDetails(
          token,
          debouncedTechTerm
        )
        const companies = Array.isArray(data) ? data : [data]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return companies.map((c: any) => ({
          label: c.technology_company_name,
          value: c.id
        }))
      },
      {
        enabled: !!debouncedTechTerm
      }
    )

  // Fetch countries using React Query
  const {
    data: countriesData,
    isLoading: countriesLoading,
    error: countriesError
  } = useQuery(
    ['countries'], // query key
    async () => {
      const token = OrbitSession.get('faberToken')
      // Replace with your actual country API call
      const { data } = await B2bCommonApi.getCountryList(token)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedCountries = data.map((country: any) => ({
        label: country.countryName, // Changed from country.name
        value: country.countryCode // Changed from country.code
      }))

      return transformedCountries
    },
    {
      staleTime: 1000 * 60 * 30, // 30 minutes - countries don't change often
      cacheTime: 1000 * 60 * 60, // 1 hour
      retry: 3,
      onError: (error) => {
        console.error('Failed to fetch countries:', error)
      }
    }
  )
  const {
    data: cityData,
    isLoading: cityLoading,
    error: cityError
  } = useQuery(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['cities', (countryWatch as any)?.value || null], // Type assertion to bypass TypeScript error
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bCommonApi.getCityList(
        token,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (countryWatch as any)?.value
      )
      // const selectedCountryValue = (countryWatch as any)?.value
      const filteredAndTransformedCities = data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((city: any) => ({
          label: city.cityName,
          value: city.cityCode,
          countryCode: city.countryCode,
          id: city.id
        }))
      return filteredAndTransformedCities
    },
    {
      staleTime: 1000 * 60 * 30,
      cacheTime: 1000 * 60 * 60,
      retry: 3,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      enabled: Boolean((countryWatch as any)?.value), // Safe boolean conversion
      onError: (error) => {
        console.error('Failed to fetch cities:', error)
      }
    }
  )

  const {
    status,
    data: tdpData,
    isLoading: tdpLoading,
    error: tdpFetchError,
    isStale,
    refetch: refetchTdp
  } = useQuery(
    ['tdpDetails', searchParams],
    async () => {
      const token = OrbitSession.get('faberToken')
      // Pass searchParams directly - it can be undefined for fetching all data
      const { data } = await B2bTDPAPI.getTDPDetails(token, searchParams)
      return data
    },
    {
      enabled: shouldFetch
    }
  )

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (formData: any) => {
    // Building query params object, only including non-empty values
    const queryParams: Record<string, string> = {}

    if (formData.companyName?.trim())
      queryParams.company_name = formData.companyName
    if (formData.contactPersonFirstName?.trim())
      queryParams.contact_person_first_name = formData.contactPersonFirstName
    if (formData.contactPersonLastName?.trim())
      queryParams.contact_person_last_name = formData.contactPersonLastName
    if (formData.country?.value) queryParams.country = formData.country.value
    if (formData.city?.value) queryParams.city = formData.city.value
    if (formData.currency?.value) queryParams.currency = formData.currency.value
    if (formData.businessType?.value)
      queryParams.business_type = formData.businessType.value
    if (formData.refSource?.value)
      queryParams.referral_source = formData.refSource.value
    if (formData['3ptp']?.value)
      queryParams.technology_company = formData['3ptp'].value
    if (formData.commercialType?.value)
      queryParams.commercial_type = formData.commercialType.value
    if (formData.accountManager?.value)
      queryParams.account_manager = formData.accountManager.value
    if (formData.status?.value)
      queryParams.status = formData.status.value.toUpperCase()

    // If no search parameters provided, set to undefined to fetch all data
    const finalParams =
      Object.keys(queryParams).length > 0 ? queryParams : undefined

    setSearchParams(finalParams)
    setShouldFetch(true)
  }

  const onReset = () => {
    reset()
    setSearchParams(undefined)
    setShouldFetch(false)
  }

  const handleRefresh = () => {
    refetchTdp()
  }

  if (tdpFetchError) {
    return (
      <Box>
        <Typography color='error'>Error loading data</Typography>
        <Button onClick={handleRefresh}>Retry</Button>
      </Box>
    )
  }

  const transformedTableData = useMemo(() => {
    if (!tdpData || !Array.isArray(tdpData)) return []
    if (status === 'success') {
      if (isStale) {
        refetchTdp()
      }
    }
    return tdpData.map((item) => ({
      id: item.id || item.companyId,
      companyName: item.companyName || item.company_name,
      contactPersonName: item.contactPersonName || item.contact_person_name,
      country: item.country,
      city: item.city,
      currency: item.currency,
      businessType: item.businessType || item.business_type,
      referralSource: item.referralSource || item.referral_source,
      commercialType: item.commercialType || item.commercial_type,
      accountManager: item.accountManager || item.account_manager,
      companyId: item.companyId || item.company_id,
      status: item.status,
      createdBy: item.createdBy || item.created_by,
      createdOn: item.createdOn || item.created_on,
      updatedBy: item.updatedBy || item.updated_by,
      updatedOn: item.updatedOn || item.updated_on,
      is_active: item.is_active
    }))
  }, [tdpData])

  // Prepare countries options with fallback
  const countryOptions = useMemo(() => {
    if (countriesError) {
      console.error('Countries fetch error:', countriesError)
      return []
    }
    return countriesData || []
  }, [countriesData, countriesError])

  // Prepare city options with fallback
  const cityOptions = useMemo(() => {
    if (cityError) {
      console.error('Cities fetch error:', cityError)
      return []
    }
    return cityData || []
  }, [cityData, cityError])

  // Clear city selection when country changes
  useEffect(() => {
    if (countryWatch) {
      setValue('city', null) // Clear city when country changes
    }
  }, [countryWatch, setValue])

  return (
    <Box>
      <Box
        sx={{
          background: '#000869',
          padding: '15px',
          borderRadius: '10px'
        }}
      >
        <Typography variant='h4' color='#fff'>
          TDP Management
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          variant='contained'
          onClick={() => navigate('tdp/registration')}
        >
          Add TDP
        </Button>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Company Name
                </Typography>
                <Controller
                  name='companyName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='companyName'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Contact Person First Name
                </Typography>
                <Controller
                  name='contactPersonFirstName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='contactPerson'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Contact Person Last Name
                </Typography>
                <Controller
                  name='contactPersonLastName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='contactPerson'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Country
                </Typography>
                <Controller
                  name='country'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={countryOptions}
                      value={field.value}
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      loading={countriesLoading}
                      // disabled={countriesError ? true : false}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {countriesLoading ? (
                                  <CircularProgress color='inherit' size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          placeholder={
                            countriesError
                              ? 'Error loading countries'
                              : countriesLoading
                              ? 'Loading...'
                              : 'Select country'
                          }
                        />
                      )}
                      noOptionsText={
                        countriesError
                          ? 'Error loading countries'
                          : countriesLoading
                          ? 'Loading...'
                          : 'No countries found'
                      }
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>City</Typography>
                <Controller
                  name='city'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={cityOptions}
                      value={field.value}
                      disabled={!countryWatch} // Disable if no country selected
                      onChange={(_, data) => field.onChange(data)}
                      loading={cityLoading}
                      size='small'
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          InputProps={{
                            ...params.InputProps
                            //
                          }}
                          placeholder={
                            !countryWatch
                              ? 'Select country first'
                              : cityError
                              ? 'Error loading cities'
                              : cityLoading
                              ? 'Loading...'
                              : 'Select city'
                          }
                        />
                      )}
                      noOptionsText={
                        !countryWatch
                          ? 'Select country first'
                          : cityError
                          ? 'Error loading cities'
                          : cityLoading
                          ? 'Loading...'
                          : 'No cities found'
                      }
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Currency
                </Typography>
                <Controller
                  name='currency'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={CurrencySupported}
                      value={field.value}
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Business Type
                </Typography>
                <Controller
                  name='businessType'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={BusinessTypes}
                      value={field.value}
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Referral Source
                </Typography>
                <Controller
                  name='refSource'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={ReferralSource}
                      value={field.value}
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Technology Company
                </Typography>
                <Controller
                  name='3ptp'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={techCompanyOptions}
                      value={field.value}
                      onChange={(_, data) => field.onChange(data)}
                      inputValue={techInput}
                      onInputChange={(_, v) => setTechInput(v)}
                      loading={techCompanyLoading}
                      getOptionLabel={(opt) => opt?.label ?? ''}
                      isOptionEqualToValue={(opt, v) =>
                        String(opt?.value) === String(v?.value)
                      }
                      size='small'
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder='Search technology company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: <>{params.InputProps.endAdornment}</>
                          }}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Commercial Type
                </Typography>
                <Controller
                  name='commercialType'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={commercialTypes}
                      value={field.value}
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>
                  Account Manager
                </Typography>
                <Controller
                  name='accountManager'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={[]}
                      value={field.value}
                      size='small'
                      onChange={(_, data) => field.onChange(data)}
                      fullWidth
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display='flex' flexDirection='column'>
                <Typography sx={{ mb: 1, fontSize: '14px' }}>Status</Typography>
                <Controller
                  name='status'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={TDPRegistrationStatus}
                      value={field.value}
                      size='small'
                      onChange={(_, data) => field.onChange(data)}
                      fullWidth
                      renderInput={(params) => <TextField {...params} />}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box mt={3} display='flex' gap={1}>
          <Button variant='contained' type='submit'>
            Search
          </Button>
          <Button variant='outlined' type='button' onClick={onReset}>
            Reset
          </Button>
        </Box>
      </form>

      {/* Only show divider and table when search has been performed */}
      {shouldFetch && (
        <>
          <Divider sx={{ m: '20px 0' }} />
          <Box>
            <CompaniesTable
              isLoading={tdpLoading}
              data={transformedTableData}
              onRefresh={handleRefresh}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default TDPManagement
